import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';

const createCheckout = async (email, uid, targetPlan, billingCycle, referralCode = '') => {
  const res = await fetch('/api/stripe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create-checkout', email, uid, targetPlan, billingCycle, referralCode }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  return data;
};

const openPortal = async (email) => {
  const res = await fetch('/api/stripe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create-portal', email }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
};

const Check = () => <span style={{ color: '#00ff99', marginRight: 6 }}>✓</span>;
const Cross = () => <span style={{ color: 'rgba(255,255,255,.25)', marginRight: 6 }}>✗</span>;

const Row = ({ label, core, pro }) => (
  <tr>
    <td style={{ padding: '9px 12px', fontSize: 13, color: 'rgba(255,255,255,.7)', borderBottom: '1px solid rgba(255,255,255,.06)' }}>{label}</td>
    <td style={{ padding: '9px 12px', fontSize: 13, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.06)' }}>{core}</td>
    <td style={{ padding: '9px 12px', fontSize: 13, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.06)' }}>{pro}</td>
  </tr>
);

// Soft info box — reassuring, neutral tone
function InfoBox({ children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)',
      border: '1px solid rgba(255,255,255,.08)',
      borderRadius: 12,
      padding: '14px 16px',
      fontSize: 13,
      color: 'rgba(255,255,255,.55)',
      lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

export default function PlansPage() {
  const { userEmail, userUid, setActiveTab } = useAppContext();
  const { planKey, billingCycle, isPro } = usePlan();

  // Billing toggle — for price display in the comparison table only
  const [tableView, setTableView] = useState('monthly');
  const isYearlyView = tableView === 'yearly';

  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualError, setAnnualError] = useState(null);
  const [annualSuccess, setAnnualSuccess] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const isMonthly = billingCycle === 'monthly';
  const isYearly = billingCycle === 'yearly';

  const handleUpgradeToPro = async () => {
    setUpgradeLoading(true);
    try {
      // Read referralCode from Firestore user doc (set during referral modal submit)
      let referralCode = '';
      try {
        const { getUserData } = await import('../firebase');
        const userData = await getUserData(userUid);
        referralCode = userData?.referralCode || '';
      } catch {}
      const data = await createCheckout(userEmail, userUid, 'pro', tableView, referralCode);
      // If already subscribed, createCheckout returns error and we redirect to portal
      if (data?.error === 'already_subscribed') {
        await openPortal(userEmail);
        return;
      }
    } catch {}
    setUpgradeLoading(false);
  };

  const handleSwitchToAnnual = async () => {
    setAnnualLoading(true);
    setAnnualError(null);
    try {
      const res = await fetch('/api/switch-to-annual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userUid }),
      });
      const data = await res.json();
      if (data.success) {
        setAnnualSuccess(true);
        // PlanContext onSnapshot will pick up the billingCycle change from Firestore
      } else if (data.error === 'annual_not_configured') {
        setAnnualError('Annual pricing for this plan isn\'t available yet. Check back soon.');
      } else if (data.error === 'already_annual') {
        setAnnualError('You\'re already on annual billing.');
      } else {
        setAnnualError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setAnnualError('Could not connect to the server. Please try again.');
    }
    setAnnualLoading(false);
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try { await openPortal(userEmail); }
    catch { setPortalLoading(false); }
  };

  const hdr = {
    padding: '10px 12px', fontSize: 13, fontWeight: 700,
    color: 'rgba(255,255,255,.5)', textAlign: 'center',
    borderBottom: '1px solid rgba(255,255,255,.1)', textTransform: 'uppercase', letterSpacing: .5,
  };

  const btnPrimary = {
    width: '100%', padding: 16, borderRadius: 12, border: 'none',
    background: 'linear-gradient(135deg,#00ff99,#00e5ff)', color: '#000',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  };

  const btnSecondary = {
    width: '100%', padding: 14, borderRadius: 12,
    border: '1px solid rgba(255,255,255,.15)',
    background: 'rgba(255,255,255,.06)', color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  };

  const btnGhost = {
    width: '100%', padding: 12, borderRadius: 12,
    border: '1px solid rgba(255,255,255,.1)',
    background: 'transparent', color: 'rgba(255,255,255,.45)',
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  };

  return (
    <div className="view">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <button onClick={() => setActiveTab('profile')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.4)', cursor: 'pointer', padding: '4px 0', fontSize: 13 }}>
          ← Back
        </button>
      </div>
      <h1 className="view-title">Plans</h1>

      {/* Billing toggle — controls price display in the table only */}
      <div style={{ display: 'flex', background: 'rgba(255,255,255,.06)', borderRadius: 10, padding: 3, marginBottom: 20, maxWidth: 280 }}>
        {['monthly', 'yearly'].map(b => (
          <button key={b} onClick={() => setTableView(b)} style={{
            flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .2s',
            background: tableView === b ? 'rgba(255,255,255,.12)' : 'transparent',
            color: tableView === b ? '#fff' : 'rgba(255,255,255,.4)',
          }}>
            {b === 'monthly' ? 'Monthly' : 'Yearly'}
          </button>
        ))}
      </div>
      {isYearlyView && (
        <div style={{ fontSize: 12, color: '#00ff99', marginBottom: 16, fontWeight: 600, lineHeight: 1.7 }}>
          Core yearly = $69.90/yr — save ~$14 (2 months free)<br />
          Pro yearly = $99.90/yr — save ~$20 (2 months free)
        </div>
      )}

      {/* Comparison table */}
      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...hdr, textAlign: 'left' }}>Feature</th>
              <th style={{ ...hdr, background: planKey === 'core' ? 'rgba(255,255,255,.04)' : 'transparent' }}>
                Core{planKey === 'core' && <div style={{ fontSize: 10, color: '#00ff99', fontWeight: 700 }}>YOUR PLAN</div>}
              </th>
              <th style={{ ...hdr, background: planKey === 'pro' ? 'rgba(0,255,153,.05)' : 'transparent' }}>
                Pro{planKey === 'pro' && <div style={{ fontSize: 10, color: '#00ff99', fontWeight: 700 }}>YOUR PLAN</div>}
              </th>
            </tr>
          </thead>
          <tbody>
            <Row label="Price" core={isYearlyView ? '$69.90/yr' : '$6.99/mo'} pro={isYearlyView ? '$99.90/yr' : '$9.99/mo'} />
            <Row label="AI Sales Coach messages" core="50/month" pro="200/month" />
            <Row label="Voice memos" core="10 max, 2 min" pro="25 max, unlimited" />
            <Row label="Voice transcription + download" core={<Cross />} pro={<Check />} />
            <Row label="Sales history" core="2 months" pro="Unlimited" />
            <Row label="PDF export" core={<Check />} pro={<Check />} />
            <Row label="Excel export" core={<Cross />} pro={<Check />} />
            <Row label="AI insights report" core={<Cross />} pro={<Check />} />
            <Row label="Win rate card" core={<Cross />} pro={<Check />} />
            <Row label="Performance trend chart" core={<Cross />} pro={<Check />} />
          </tbody>
        </table>
      </div>

      {/* ── CTA section — varies by plan + billing cycle ── */}
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* ── PRO MONTHLY ─────────────────────────────────────── */}
        {isPro && isMonthly && (
          <>
            {annualSuccess ? (
              <InfoBox>
                <span style={{ color: '#00ff99', fontWeight: 700 }}>Switched to annual billing.</span> Your card will be charged the prorated difference now. Enjoy the savings!
              </InfoBox>
            ) : (
              <>
                <button
                  onClick={handleSwitchToAnnual}
                  disabled={annualLoading}
                  style={btnPrimary}
                >
                  {annualLoading ? 'Switching...' : 'Switch to Yearly & Save $20 →'}
                </button>
                {annualError && (
                  <div style={{ fontSize: 13, color: 'rgba(255,120,120,.9)', textAlign: 'center' }}>
                    {annualError}
                  </div>
                )}
              </>
            )}
            <DowngradeDisclaimer onPortal={handlePortal} portalLoading={portalLoading} />
          </>
        )}

        {/* ── PRO YEARLY ──────────────────────────────────────── */}
        {isPro && isYearly && (
          <DowngradeDisclaimer onPortal={handlePortal} portalLoading={portalLoading} />
        )}

        {/* ── CORE MONTHLY ────────────────────────────────────── */}
        {!isPro && isMonthly && (
          <>
            <button
              onClick={handleUpgradeToPro}
              disabled={upgradeLoading}
              style={btnPrimary}
            >
              {upgradeLoading ? 'Redirecting...' : `Upgrade to Pro — $${isYearlyView ? '99.90/yr' : '9.99/mo'}`}
            </button>

            {annualSuccess ? (
              <InfoBox>
                <span style={{ color: '#00ff99', fontWeight: 700 }}>Switched to annual billing.</span> Your card will be charged the prorated difference now.
              </InfoBox>
            ) : (
              <>
                <button
                  onClick={handleSwitchToAnnual}
                  disabled={annualLoading}
                  style={btnSecondary}
                >
                  {annualLoading ? 'Switching...' : 'Switch to Annual — $69.90/yr (save $14)'}
                </button>
                {annualError && (
                  <div style={{ fontSize: 13, color: 'rgba(255,120,120,.9)', textAlign: 'center' }}>
                    {annualError}
                  </div>
                )}
              </>
            )}

            <CancelInfoBox onPortal={handlePortal} portalLoading={portalLoading} />
          </>
        )}

        {/* ── CORE YEARLY ─────────────────────────────────────── */}
        {!isPro && isYearly && (
          <>
            <button
              onClick={handleUpgradeToPro}
              disabled={upgradeLoading}
              style={btnPrimary}
            >
              {upgradeLoading ? 'Redirecting...' : 'Upgrade to Pro — $99.90/yr'}
            </button>
            <CancelInfoBox onPortal={handlePortal} portalLoading={portalLoading} />
          </>
        )}

      </div>

      {/* Team coming soon */}
      <div style={{
        marginTop: 20, padding: '14px 16px', background: 'rgba(255,255,255,.02)',
        border: '1px dashed rgba(255,255,255,.1)', borderRadius: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,.4)', fontWeight: 600 }}>Tracksy Team</div>
        <div style={{ fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.4)', padding: '3px 10px', borderRadius: 20 }}>Coming Soon</div>
      </div>
    </div>
  );
}

// ── Downgrade disclaimer for Pro subscribers ───────────────────────────────────
function DowngradeDisclaimer({ onPortal, portalLoading }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)',
      border: '1px solid rgba(255,255,255,.08)',
      borderRadius: 12,
      padding: '16px',
    }}>
      <p style={{
        margin: '0 0 12px', fontSize: 13, color: 'rgba(255,255,255,.55)', lineHeight: 1.6,
      }}>
        To switch to a lower plan, cancel your current subscription — it will remain active until the end of your billing period. When you're ready to resubscribe, sign up with the same email address. All your data, history, and settings will be right where you left them.
      </p>
      <button
        onClick={onPortal}
        disabled={portalLoading}
        style={{
          width: '100%', padding: '10px 0', borderRadius: 10,
          border: '1px solid rgba(255,80,80,.3)',
          background: 'rgba(255,80,80,.06)', color: 'rgba(255,130,130,.9)',
          fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {portalLoading ? 'Opening...' : 'Cancel Subscription'}
      </button>
    </div>
  );
}

// ── Cancel info for Core subscribers ──────────────────────────────────────────
function CancelInfoBox({ onPortal, portalLoading }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)',
      border: '1px solid rgba(255,255,255,.08)',
      borderRadius: 12,
      padding: '16px',
    }}>
      <p style={{
        margin: '0 0 12px', fontSize: 13, color: 'rgba(255,255,255,.55)', lineHeight: 1.6,
      }}>
        To stop billing, you can cancel your subscription at any time. Your data and history will be saved and available if you return.
      </p>
      <button
        onClick={onPortal}
        disabled={portalLoading}
        style={{
          width: '100%', padding: '10px 0', borderRadius: 10,
          border: '1px solid rgba(255,255,255,.1)',
          background: 'transparent', color: 'rgba(255,255,255,.4)',
          fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {portalLoading ? 'Opening...' : 'Manage billing / cancel subscription'}
      </button>
    </div>
  );
}
